import { forwardRef, useImperativeHandle, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useQuery } from "react-query";

import { CFormGroup, CLabel } from "@coreui/react";

import { nhaCungCapApi } from "src/1/apis/nha_cung_cap.api";
import { phieuChamDiemNCCApi } from "src/1/apis/phieu_cham_diem_nha_cung_cap.api";
import { CButton, CRating, CSelect } from "src/common/components/controls";

import { CDialog } from "_components/others";

import { FilesCell } from "./FilesCell";

const defaultValues = {
  supplier_id: "",
  financial: 5,
  reputation: 5,
  quality: 5,
  pricing: 5,
  customer_review: 5,
  branch_review: 5,
  materials: [],
  files: [],
};

export const FormModal = forwardRef(({ formId, refetch }, ref) => {
  //#region Data
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("create"); // "create" | "edit"

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isSubmitting },
  } = useForm({ defaultValues });

  const { data: suppliers = [] } = useQuery({
    queryKey: ["danh-sach-nha-cung-cap"],
    queryFn: () => nhaCungCapApi.getAll(),
    select: (response) =>
      response?.data?.data?.map((e) => ({
        id: e?.id,
        value: e?.id,
        code: e?.code,
        label: e?.name,
        materials: e?.materials || [],
      })),
  });
  //#endregion

  //#region Event
  const onClose = () => {
    setOpen(false);
    setMode("create");
    reset(defaultValues);
  };

  const onSubmit = handleSubmit(async (values) => {
    try {
      const payload = { ...values, files: values?.files?.map((e) => e.id) };
      if (mode === "create") {
        await phieuChamDiemNCCApi.addSupplier(formId, payload);
      } else {
        const { id } = values;
        await phieuChamDiemNCCApi.updateSupplier(formId, id, payload);
      }
      noti("success", "Lưu thông tin nhà cung cấp thành công!");
      refetch?.();
      onClose();
    } catch (error) {
      noti(
        "error",
        error?.message ?? "Lưu thông tin nhà cung cấp không thành công!"
      );
    }
  });

  const onSelectSupplier = (_supplierId, selectedOption) => {
    setValue(
      "materials",
      selectedOption?.materials?.map((e) => e.name)
    );
  };
  //#endregion

  useImperativeHandle(ref, () => ({
    open: (editData) => {
      if (editData) {
        setMode("edit");
        reset({
          id: editData?.id,
          supplier_id: editData?.supplier_id,
          financial: editData?.financial,
          reputation: editData?.reputation,
          quality: editData?.quality,
          pricing: editData?.pricing,
          customer_review: editData?.customer_review,
          branch_review: editData?.branch_review,
          materials: editData?.materials?.map((e) => e?.name),
          files: editData?.files,
        });
      }
      setOpen(true);
    },
  }));

  //#region Render
  return (
    <CDialog
      size="lg"
      show={open}
      onClose={onClose}
      title={`${mode === "create" ? "Thêm" : "Sửa"} nhà cung cấp`}
      footer={
        <div className="flex flex-row gap-3">
          <CButton
            variant="outline"
            color="danger"
            disabled={isSubmitting}
            onClick={onClose}
          >
            Hủy bỏ
          </CButton>
          <CButton disabled={isSubmitting} onClick={onSubmit}>
            Lưu
          </CButton>
        </div>
      }
    >
      <div className="grid grid-cols-4 gap-5">
        <Controller
          control={control}
          name="supplier_id"
          render={({ field }) => (
            <CSelect
              {...field}
              options={suppliers}
              placeholder="Chọn nhà cung cấp"
              label="Mã NCC"
              select="value"
              display="code"
              required
              className="col-span-2"
              onChangeCallback={onSelectSupplier}
            />
          )}
        />
        <Controller
          control={control}
          name="supplier_id"
          render={({ field }) => (
            <CSelect
              {...field}
              options={suppliers}
              placeholder="Chọn nhà cung cấp"
              label="Mã NCC"
              select="value"
              required
              className="col-span-2"
              onChangeCallback={onSelectSupplier}
            />
          )}
        />
        <Controller
          control={control}
          name="financial"
          render={({ field }) => (
            <CFormGroup>
              <CLabel>Năng lực tài chính:</CLabel>
              <CRating value={field.value} onChange={field.onChange} />
            </CFormGroup>
          )}
        />
        <Controller
          control={control}
          name="reputation"
          render={({ field }) => (
            <CFormGroup>
              <CLabel>Uy tín NCC:</CLabel>
              <CRating value={field.value} onChange={field.onChange} />
            </CFormGroup>
          )}
        />
        <Controller
          control={control}
          name="quality"
          render={({ field }) => (
            <CFormGroup>
              <CLabel>Chất lượng:</CLabel>
              <CRating value={field.value} onChange={field.onChange} />
            </CFormGroup>
          )}
        />
        <Controller
          control={control}
          name="pricing"
          render={({ field }) => (
            <CFormGroup>
              <CLabel>Giá cả:</CLabel>
              <CRating value={field.value} onChange={field.onChange} />
            </CFormGroup>
          )}
        />
        <Controller
          control={control}
          name="customer_review"
          render={({ field }) => (
            <CFormGroup>
              <CLabel>Đánh giá từ KH:</CLabel>
              <CRating value={field.value} onChange={field.onChange} />
            </CFormGroup>
          )}
        />
        <Controller
          control={control}
          name="branch_review"
          render={({ field }) => (
            <CFormGroup>
              <CLabel>Đánh giá từ chi nhánh:</CLabel>
              <CRating value={field.value} onChange={field.onChange} />
            </CFormGroup>
          )}
        />
        <Controller
          control={control}
          name="materials"
          render={({ field }) => (
            <CFormGroup className="col-span-2">
              <CLabel>Danh sách NVL cung cấp:</CLabel>
              <p className="whitespace-pre-wrap font-medium text-base">
                {field.value.join(", ")}
              </p>
            </CFormGroup>
          )}
        />
        <Controller
          control={control}
          name="files"
          render={() => (
            <CFormGroup className="col-span-2">
              <CLabel>Tài liệu minh chứng:</CLabel>
              <FilesCell control={control} />
            </CFormGroup>
          )}
        />
      </div>
    </CDialog>
  );
  //#endregion
});
